import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { collection, doc, writeBatch, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { Product, standardizeProduct } from '../utils/productUtils';

export const useProductImport = (
  onSuccess?: (message: string) => void,
  onError?: (error: string) => void,
  onFinish?: () => void
) => {
  const { currentUser, effectiveScopeUid } = useAuth();
  const [importData, setImportData] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [useFileUpload, setUseFileUpload] = useState(true);
  
  // Datei-Einlesen-Handler
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setImportData(event.target.result.toString());
        }
      };
      reader.readAsText(file);
    }
  }, []);
  
  // Text-Eingabe-Handler
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setImportData(e.target.value);
  }, []);
  
  // Toggles zwischen Datei-Upload und direkter Texteingabe
  const toggleInputMode = useCallback(() => {
    setUseFileUpload(prev => !prev);
  }, []);
  
  // Parst CSV- oder JSON-Daten in Produkte
  const parseImportData = useCallback((data: string): Partial<Product>[] => {
    if (!data.trim()) return [];
    
    let importedProducts: Partial<Product>[] = [];
    
    // Versuche, die Daten als JSON zu parsen
    try {
      importedProducts = JSON.parse(data);
      if (!Array.isArray(importedProducts)) {
        importedProducts = [importedProducts];
      }
      return importedProducts;
    } catch (err) {
      // Wenn kein gültiges JSON, versuche CSV zu parsen
      // 1. BOM entfernen und Zeilenumbrüche normalisieren
      const cleanData = data.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = cleanData.split('\n').filter(line => line.trim() !== '');
      
      if (lines.length < 2) return []; // Mindestens Header und eine Datenzeile
      
      // Erkennung des Trennzeichens (Semikolon oder Komma)
      const firstLine = lines[0];
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      const commaCount = (firstLine.match(/,/g) || []).length;
      const delimiter = semicolonCount > commaCount ? ';' : ',';
      
      // Helper Funktion zum Parsen einer CSV Zeile (beachtet Quotes)
      const parseLine = (line: string): string[] => {
        const values: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++; // Überspringe das nächste Quote
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === delimiter && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        return values;
      };
      
      // Header parsen und bereinigen
      const headers = parseLine(firstLine).map(h => h.replace(/^"|"$/g, '').trim());
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = parseLine(lines[i]);
        
        // Werte bereinigen (Quotes entfernen)
        const cleanValues = values.map(v => v.replace(/^"|"$/g, ''));
        
        const product: Partial<Product> = {};
        
        headers.forEach((header, index) => {
          if (index >= cleanValues.length) return;
          
          const value = cleanValues[index];
          if (value) {
            // Key-Generierung: Nur Buchstaben (auch Unicode) und Zahlen behalten
            const normalizedKey = header.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
            
            let targetKey: keyof Product | null = null;
            
            // Mapping für alle unterstützten Sprachen
            switch(normalizedKey) {
              // Name
              case 'name':
              case 'productname':
              case 'produktname':
              case 'nomduproduit':
              case 'nazwaproduktu':
              case 'названиепродукта':
              case 'nombredelproducto':
              case 'nomeprodotto':
              case 'numeprodus':
                targetKey = 'name';
                break;
                
              // Description
              case 'description':
              case 'beschreibung':
              case 'ladescription':
              case 'opis':
              case 'описание':
              case 'descripcion':
              case 'descrizione':
              case 'descriere':
                targetKey = 'description';
                break;
                
              // Net Price
              case 'netprice':
              case 'price':
              case 'preis':
              case 'nettopreis':
              case 'prix':
              case 'prixnet':
              case 'cena':
              case 'cenanetto':
              case 'цена':
              case 'неттоцена':
              case 'precio':
              case 'precioneto':
              case 'prezzo':
              case 'prezzonetto':
              case 'pret':
              case 'pretnet':
                targetKey = 'netPrice';
                break;
                
              // Tax Rate
              case 'taxrate':
              case 'tax':
              case 'vat':
              case 'steuer':
              case 'steuersatz':
              case 'mwst':
              case 'taxe':
              case 'tauxdetaxe':
              case 'tva':
              case 'podatek':
              case 'stawkapodatku':
              case 'налог':
              case 'ставка налога':
              case 'ндс':
              case 'impuesto':
              case 'tasadeimpuesto':
              case 'iva':
              case 'tassa':
              case 'aliquotafiscale':
              case 'taxa':
              case 'cotatva':
                targetKey = 'taxRate';
                break;
                
              // Unit Code
              case 'unitcode':
              case 'unit':
              case 'einheit':
              case 'einheitscode':
              case 'unite':
              case 'codeunite':
              case 'jednostka':
              case 'kodjednostki':
              case 'единица':
              case 'кодединицы':
              case 'unidad':
              case 'codigodeunidad':
              case 'unita':
              case 'codiceunita':
              case 'unitate':
              case 'codunitate':
                targetKey = 'unitCode';
                break;
                
              // Seller Article ID
              case 'sellerarticleid':
              case 'articleid':
              case 'artikelnummer':
              case 'nummer':
              case 'numerodarticle':
              case 'numerartykulu':
              case 'артикул':
              case 'номерстатьи':
              case 'numerodearticulo':
              case 'numeroarticolo':
              case 'numararticol':
                targetKey = 'sellerArticleId';
                break;
            }
            
            if (targetKey) {
              if (targetKey === 'netPrice' || targetKey === 'taxRate') {
                // Zahlen parsen (Komma zu Punkt konvertieren)
                const numValue = parseFloat(value.replace(',', '.'));
                (product as any)[targetKey] = isNaN(numValue) ? 0 : numValue;
              } else {
                (product as any)[targetKey] = value;
              }
            }
          }
        });
        
        if (Object.keys(product).length > 0) {
          importedProducts.push(product);
        }
      }
      
      return importedProducts;
    }
  }, []);
  
  // Import durchführen
  const executeImport = useCallback(async () => {
    if (!currentUser || !importData.trim()) {
      if (onError) onError('importError:noData');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Parsen der Import-Daten
      const productsToImport = parseImportData(importData);
      
      if (productsToImport.length === 0) {
        if (onError) onError('importError:noValidProducts');
        return;
      }
      
      // Bestehende Produkte laden für Duplikatsprüfung (effizienter als loop-query)
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('userId', '==', effectiveScopeUid));
      const querySnapshot = await getDocs(q);
      
      const existingProductsMap = new Map<string, string>(); // Name -> ID
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.name) {
          // Normalisiere Name für Vergleich (trim + lowercase)
          existingProductsMap.set(data.name.trim().toLowerCase(), doc.id);
        }
      });

      // Batch-Verarbeitung für Firestore
      const batchSize = 450;
      const chunks = [];
      
      for (let i = 0; i < productsToImport.length; i += batchSize) {
        chunks.push(productsToImport.slice(i, i + batchSize));
      }
      
      let successCount = 0;
      const newIds: string[] = [];
      const updatedIds: string[] = [];
      
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        
        for (const product of chunk) {
          if (!product.name) continue;
          
          // Standardisiere das Produkt
          const standardizedProduct = standardizeProduct(product);
          const normalizedName = standardizedProduct.name.trim().toLowerCase();
          
          // Prüfe ob Produkt bereits existiert
          const existingId = existingProductsMap.get(normalizedName);
          
          if (existingId) {
            // Update existierendes Produkt
            const productRef = doc(db, 'products', existingId);
            batch.update(productRef, {
              ...standardizedProduct,
              updatedAt: serverTimestamp()
            });
            updatedIds.push(existingId);
          } else {
            // Erstelle neues Produkt
            const newProductRef = doc(collection(db, 'products'));
            batch.set(newProductRef, {
              ...standardizedProduct,
              userId: effectiveScopeUid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            newIds.push(newProductRef.id);
            // Füge neu erstelltes Produkt zur Map hinzu, falls es im gleichen Batch/Import doppelt vorkommt
            existingProductsMap.set(normalizedName, newProductRef.id);
          }
          
          successCount++;
        }
        
        await batch.commit();
      }
      
      // Erfolgsmeldung
      if (onSuccess) {
        onSuccess(`importSuccess:${successCount}:${newIds.length}:${updatedIds.length}`);
      }
      
      // Daten zurücksetzen
      setImportData('');
      
      // Callback nach Abschluss
      if (onFinish) onFinish();
      
    } catch (err: any) {
      console.error('Fehler beim Import:', err);
      if (onError) onError(`importError:${err.message || 'unknownError'}`);
    } finally {
      setIsProcessing(false);
    }
  }, [currentUser, effectiveScopeUid, importData, parseImportData, onSuccess, onError, onFinish]);
  
  return {
    importData,
    setImportData,
    fileName,
    isProcessing,
    useFileUpload,
    setUseFileUpload,
    handleFileChange,
    handleTextChange,
    toggleInputMode,
    executeImport
  };
}; 